import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { Play, Star } from "lucide-react";
import { FloatingDecorations } from "@/components/FloatingDecorations";

interface InteractiveHeroSectionProps {
  onPlayNow: () => void;
  onPlayOnline?: () => void;
}

export function InteractiveHeroSection({ onPlayNow, onPlayOnline }: InteractiveHeroSectionProps) {
  const [currentTagline, setCurrentTagline] = useState(0);

  const taglines = [
    { text: "Learn, Play, Master Chess! 🎯", color: "text-teen-green" },
    { text: "Your chess adventure starts here! ✨", color: "text-teen-blue" },
    { text: "From beginner to master! 🏆", color: "text-teen-purple" },
    { text: "Discover the beauty of chess! ♟️", color: "text-teen-gold" }
  ];

  // Rotate taglines every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-teal-100 via-emerald-100 to-cyan-100 dark:from-purple-900/40 dark:via-pink-900/20 dark:to-cyan-900/30 rounded-3xl border-2 border-primary/30 p-4 md:p-6 lg:p-8 mobile-safe-container mx-auto max-w-full shadow-vibrant dark:animate-magic-glow">
      {/* Enchanted floating decorations */}
      <FloatingDecorations density="high" />
      
      {/* Animated floating orbs - only in dark mode */}
      <div className="hidden dark:block absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-4 md:top-8 right-4 md:right-8 w-24 h-24 md:w-40 md:h-40 bg-gradient-to-br from-teen-cyan/40 to-teen-blue/30 rounded-full blur-2xl animate-float" />
        <div className="absolute bottom-4 md:bottom-8 left-4 md:left-8 w-20 h-20 md:w-32 md:h-32 bg-gradient-to-br from-teen-pink/35 to-teen-purple/25 rounded-full blur-2xl animate-float" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-1/2 left-1/4 w-16 h-16 md:w-24 md:h-24 bg-gradient-to-br from-teen-gold/30 to-teen-orange/20 rounded-full blur-xl animate-float" style={{ animationDelay: '3s' }} />
        
        {/* Extra sparkle emojis */}
        <span className="absolute top-[15%] left-[10%] text-2xl animate-twinkle" style={{ animationDelay: '0s' }}>✨</span>
        <span className="absolute top-[25%] right-[15%] text-3xl animate-twinkle" style={{ animationDelay: '0.5s' }}>⭐</span>
        <span className="absolute bottom-[30%] left-[20%] text-xl animate-twinkle" style={{ animationDelay: '1s' }}>💫</span>
        <span className="absolute bottom-[20%] right-[25%] text-2xl animate-twinkle" style={{ animationDelay: '1.5s' }}>🌟</span>
        <span className="absolute top-[60%] left-[5%] text-lg animate-twinkle" style={{ animationDelay: '2s' }}>✨</span>
        <span className="absolute top-[40%] right-[5%] text-xl animate-twinkle" style={{ animationDelay: '2.5s' }}>⭐</span>
      </div>
      
      {/* Shimmer overlay - only in dark mode */}
      <div className="hidden dark:block absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer rounded-3xl pointer-events-none" style={{ backgroundSize: '200% 100%' }} />

      <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6 md:space-y-8 px-2">
        {/* Logo */}
        <div className="flex justify-center">
          <Logo size="xl" className="h-20 md:h-24" loading="eager" />
        </div>

        {/* Rotating taglines with gradient text */}
        <div className="space-y-4">
          <h1 className={`text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold transition-all duration-500 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent px-2`}>
            {taglines[currentTagline].text}
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-foreground/70 max-w-2xl mx-auto leading-relaxed px-4">
            The most fun way to learn and play chess! Challenge AI opponents and start your chess journey today.
          </p>
        </div>

        {/* CTA buttons - vibrant */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {onPlayOnline && (
            <Button 
              onClick={onPlayOnline}
              size="lg"
              className="px-8 py-4 rounded-xl text-lg bg-gradient-to-r from-primary to-accent text-white hover:from-primary/90 hover:to-accent/90 shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Star className="w-5 h-5 mr-2" />
              Play Online! 🌐
            </Button>
          )}
          <Button 
            onClick={onPlayNow}
            size="lg"
            className="px-8 py-4 rounded-xl text-lg bg-gradient-to-r from-accent to-primary text-white hover:from-accent/90 hover:to-primary/90 shadow-lg hover:scale-105 transition-all duration-300"
          >
            <Play className="w-5 h-5 mr-2" />
            Challenge AI Bot! 🤖
          </Button>
        </div>
      </div>
    </div>
  );
}